<template>
  <div>
    <h2 class="mb-4">
      Review Calls for Dispute Payment Adjustment
    </h2>

    <CallsList
      :calls="selectedCalls"
      :loading="loading"
      hide-adjustment-action
    >
      <template #actions="{ call }">
        <CButton
          color="info"
          size="sm"
          class="mt-1"
          @click="unselectCall(call)"
        >
          Unselect
        </CButton>
      </template>
    </CallsList>

    <CCard>
      <CCardHeader>
        Set Payment for Selected Calls
      </CCardHeader>

      <CCardBody>
        <CForm
          novalidate
          @submit.prevent
        >
          <CInput
            v-model="$v.disputeDetails.reason.$model"
            name="Reason"
            label="Reason by Buyer/Seller"
            horizontal
            :is-valid="checkIfValid(['disputeDetails', 'reason'])"
            invalid-feedback="Please mention reason specified by the client"
          />

          <CTextarea
            v-model="$v.disputeDetails.reviewComments.$model"
            name="Review Comments"
            label="Review Comments"
            horizontal
            rows="4"
            :is-valid="checkIfValid(['disputeDetails', 'reviewComments'])"
            invalid-feedback="Please add review comments"
          />

          <CInput
            v-model="$v.disputeDetails.newConversionAmount.$model"
            name="Conversion"
            label="New Conversion Amount"
            horizontal
            type="number"
            add-input-classes="w-fixed-130"
            :is-valid="checkIfValid(['disputeDetails', 'newConversionAmount'])"
            invalid-feedback="Conversion cannot be less than $0"
          />

          <CInput
            v-model="$v.disputeDetails.newPayoutAmount.$model"
            name="Payout"
            label="New Payout Amount"
            horizontal
            type="number"
            add-input-classes="w-fixed-130"
            :is-valid="checkIfValid(['disputeDetails', 'newPayoutAmount'])"
            invalid-feedback="Payout cannot be less than $0"
          />

          <CFormGroup class="form-group form-row">
            <template #label>
              <slot name="label">
                <label class="col-form-label col-sm-3">
                  Charge Buyer Dispute Fees Per Call
                </label>
              </slot>
            </template>

            <template #input>
              <CSwitch
                shape="pill"
                color="primary"
                horizontal
                class="mt-1"
                add-input-classes="w-25"
                :checked.sync="disputeDetails.chargeBuyerDisputeFees"
              />

              <CInput
                v-if="disputeDetails.chargeBuyerDisputeFees"
                v-model="$v.disputeDetails.buyerDisputeFees.$model"
                name="Buyer Dispute Fees"
                type="number"
                class="mb-0"
                add-input-classes="w-25 ml-3"
                horizontal
                :is-valid="checkIfValid(['disputeDetails', 'buyerDisputeFees'])"
              >
                <template #append-content>
                  Total fees: {{ formatAmount(disputeDetails.buyerDisputeFees * selectedCalls.length) }}
                </template>
              </CInput>
            </template>
          </CFormGroup>
          

          <CFormGroup class="form-group form-row">
            <template #label>
              <slot name="label">
                <label class="col-form-label col-sm-3">
                  Charge Seller Dispute Fees Per Call
                </label>
              </slot>
            </template>

            <template #input>
              <CSwitch
                shape="pill"
                color="primary"
                horizontal
                class="mt-1"
                add-input-classes="w-25"
                :checked.sync="disputeDetails.chargeSellerDisputeFees"
              />

              <CInput
                v-if="disputeDetails.chargeSellerDisputeFees"
                v-model="$v.disputeDetails.sellerDisputeFees.$model"
                name="Seller Dispute Fees"
                class="mb-0"
                type="number"
                add-input-classes="w-25 ml-3"
                horizontal
                :is-valid="checkIfValid(['disputeDetails', 'sellerDisputeFees'])"
              >
                <template #append-content>
                  Total fees: {{ formatAmount(disputeDetails.sellerDisputeFees * selectedCalls.length) }}
                </template>
              </CInput>
            </template>
          </CFormGroup>

          <div class="form-row">
            <CCol
              :md="{ offset: '3', size: '9' }"
              :sm="{offset: '3', size: '9'}"
            >
              <ButtonLoading
                color="primary"
                type="submit"
                @click="showAdjustmentConfirmationModal"
              >
                Adjust
              </ButtonLoading>

              <CButton
                class="ml-2"
                type="button"
                color="danger"
                @click="goBackFromAdjustmentPage"
              >
                Cancel
              </CButton>
            </CCol>
          </div>
        </CForm>
      </CCardBody>
    </CCard>

    <CModal
      title="Confirm Adjust Payment for Calls"
      color="warning"
      :show.sync="showConfirmationModal"
    >
      Are you sure you want to adjust payment for dispute for <strong>{{ selectedCalls.length }}</strong> call(s)?

      <dl class="row mt-3">
        <dt class="col-sm-4 text-right">
          New Conversion
        </dt>
        <dd class="col-sm-7 d-flex align-items-center">
          {{ formatAmount(disputeDetails.newConversionAmount) }}
        </dd>
        <dt class="col-sm-4 text-right">
          New Payout
        </dt>
        <dd class="col-sm-7 d-flex align-items-center">
          {{ formatAmount(disputeDetails.newPayoutAmount) }}
        </dd>
      </dl>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isLoading"
          @click="onConfirmSubmit"
        >
          Adjust
        </ButtonLoading>

        <CButton
          color="danger"
          @click="showConfirmationModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import moment from 'moment'
import { required, minValue } from 'vuelidate/lib/validators'

import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import formGenericMixin from '@/mixins/form-generic-mixin'
import CallsList from '@/views/Calls/CallsList.vue'
import { 
  formatDate,
  formatAmount,
} from '@/utils'
import DisputesApi from '@/api/disputes.api'

export default {
  name: 'DisputesManualAdjustments',

  title: 'Dispute Adjustments',

  components: {
    CallsList,
  },

  mixins: [formGenericMixin],

  props: {
    callIdsCommaSeparated: {
      type: String,
      required: true
    },
    dateRangeCommaSeparated: { // eg. 2021-08-30, 2021-09-01
      type: String,
      required: true
    },
  },

  validations() {
    return {
      disputeDetails: {
        reason: {
          required
        },
        reviewComments: {
          required
        },
        newConversionAmount: {
          required,
          minValue: minValue(0)
        },
        newPayoutAmount: {
          required,
          minValue: minValue(0)
        },
        buyerDisputeFees: {
          minValue: minValue(0)
        },
        sellerDisputeFees: {
          minValue: minValue(0)
        },
      }
    }
  },

  data() {
    return {
      prevRoute: null,

      dateRangeParsed: [],
      selectedCalls: [],

      showConfirmationModal: false,
      isLoading: false,

      disputeDetails: {
        reason: '',
        reviewComments: '',
        newConversionAmount: 10,
        newPayoutAmount: 10,
        chargeSellerDisputeFees: false,
        chargeBuyerDisputeFees: false,
        buyerDisputeFees: 0,
        sellerDisputeFees: 0
      }
    }
  },

  computed: {
    ...mapState('callLogs', [
      'searchForCallIds',
      'dateFrom',
      'dateTo',
      'calls'
    ]),

    ...mapState('callLogs', {
      loading: state => state.isLoading
    }),

    dateRange: {
      get() {
        return {
          startDate: this.selectedDateFrom,
          endDate: this.selectedDateTo
        }
      },
      set({ startDate, endDate }) {
        this.selectedDateFrom = moment(startDate).startOf('day').toDate()
        this.selectedDateTo = moment(endDate).endOf('day').toDate()
      }
    },

    selectedDateFrom: {
      get() {
        return this.dateFrom
      },
      set(value) {
        this.SET_DATE_FROM(value)
      }
    },

    selectedDateTo: {
      get() {
        return this.dateTo
      },
      set(value) {
        this.SET_DATE_TO(value)
      }
    },

    callIds: {
      get() {
        return this.searchForCallIds
      },
      set(value) {
        this.SET_CALL_IDS(value)
      }
    },
  },

  watch: {
    async callIdsCommaSeparated() {
      await this.checkAndFetchCallsIfNeeded()
    },

    async dateRangeCommaSeparated() {
      await this.checkAndFetchCallsIfNeeded()
    },

    calls: {
      immediate: true,
      handler(newValue) {
        const callIds = this.callIdsCommaSeparated?.split(',') ?? []
        this.selectedCalls = newValue?.filter(c => callIds.indexOf(c.callId) > -1) ?? []
      }
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  async created () {
    await this.checkAndFetchCallsIfNeeded()
  },

  methods: {
    ...mapActions('callLogs', [
      'searchForCalls'
    ]),

    ...mapMutations('callLogs', [
      'SET_DATE_FROM',
      'SET_DATE_TO',
      'SET_CALL_IDS',
      'SET_CALLER_IDS',
    ]),

    formatDate,

    formatAmount,

    async checkAndFetchCallsIfNeeded() {
      if (!this.callIdsCommaSeparated) {
        showErrorMessage('Please select call Ids')
        return
      }

      this.callIds = this.callIdsCommaSeparated

      if (this.dateRangeCommaSeparated) {
        this.dateRangeParsed = [moment(this.dateRangeCommaSeparated.split(',')[0]).startOf('day').toDate(), moment(this.dateRangeCommaSeparated.split(',')[1] ?? new Date()).endOf('day').toDate()]

        this.selectedDateFrom = this.dateRangeParsed[0]
        this.selectedDateTo = this.dateRangeParsed[1]
      } else {
        showErrorMessage('No date range specified')
        return
      }

      // Fetch calls from API if not already fetched previously
      if (this.callIdsCommaSeparated.split(',').some(id => !this.calls.find(call => call.callId === id))) {
        await this.searchForCalls()
      }

      this.setDefaultPaymentForAdjustment()
    },

    setDefaultPaymentForAdjustment() {
      const firstCallFromSelection = this.selectedCalls[0]

      this.disputeDetails.newConversionAmount = firstCallFromSelection?.conversionAmount ?? 0
      this.disputeDetails.newPayoutAmount = firstCallFromSelection?.payoutAmount ?? 0
    },

    unselectCall(call) {
      this.selectedCalls = [...this.selectedCalls.filter(c => c.callId !== call.callId)]

      if (!this.selectedCalls?.length) {
        showErrorMessage('Please select at least one call to adjust payment')
        this.goBackFromAdjustmentPage()
      }
    },

    goBackFromAdjustmentPage() {
      if (this.prevRoute?.name) {
        return this.$router.go(-1)
      } else {
        this.$router.push({name: 'Reporting'})
      }
    },

    showAdjustmentConfirmationModal() {
      this.submitted = true
      this.$v.disputeDetails.$touch()

      if (!this.selectedCalls?.length) {
        showErrorMessage('Please select at least one call to adjust payment')
        this.goBackFromAdjustmentPage()
        return
      }

      const alreadyDisputedCall = this.selectedCalls.find(call => !!call.disputedConversionAdjustmentAmount)

      if (alreadyDisputedCall) {
        showErrorMessage(`${alreadyDisputedCall.callId} has already been disputed. You cannot make adjustment for this call.`)
        return
      }

      if (this.isValid) {
        this.showConfirmationModal = true
      }
    },

    async onConfirmSubmit() {
      try {
        this.isLoading = true

        await DisputesApi.adjustCallsPaymentsForDispute({
          callIds: this.selectedCalls.map(call => call.callId),
          ...this.disputeDetails
        })

        this.showConfirmationModal = false

        showSuccessMessage('Successfully requested for dispute adjustment, you\'ll be notified once it has been processed')

        this.goBackFromAdjustmentPage()
      } catch (error) {
        //
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>
<style lang="scss" scoped></style>